import React, { useState } from 'react';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import { supabaseClient } from '../../config/supabase';
import { describeResetResult, resetTestContact } from '../../lib/testContactReset';

// BL-177 Step 2o: the confirm step for "Reset test contact" (Test list popup and a thread's "..." menu).
// Archives first, then clears that one phone / email back to a first-time visitor. Only company admins get here (the buttons are hidden otherwise).
export default function ResetTestContactDialog({ address, onClose, onDone, client = supabaseClient }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [removePortalAccess, setRemovePortalAccess] = useState(false);
    const [conversationOnly, setConversationOnly] = useState(false);

    async function confirm() {
        setBusy(true);
        setError('');
        try {
            const done = await resetTestContact(client, address, { removePortalAccess, conversationOnly });
            setResult(describeResetResult(done));
            onDone?.(done);
        } catch (failure) {
            setError(failure?.message || 'That test contact could not be reset. Nothing changed.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <Modal3D
            className="ia-settings-popup"
            title="Reset this test contact?"
            subtitle={address}
            stripe={result ? 'teal' : 'red'}
            portal
            testId="reset-test-contact-dialog"
            dismissible={!busy}
            onClose={onClose}
            actions={result ? (
                <Button3D variant="go" onClick={onClose}>Close</Button3D>
            ) : (
                <>
                    <Button3D type="button" onClick={onClose} disabled={busy}>Cancel</Button3D>
                    <Button3D type="button" variant="go" onClick={confirm} disabled={busy} data-testid="reset-test-contact-confirm">{busy ? 'Resetting…' : conversationOnly ? 'Reset AI conversation only' : 'Reset test contact'}</Button3D>
                </>
            )}
        >
            {result ? (
                <div data-testid="reset-test-contact-result" className="space-y-1 text-sm">
                    <p className="font-bold">Done.</p>
                    <p>{result.cleared}</p>
                    {result.skipped && <p className="text-[#FCD34D]">{result.skipped}</p>}
                    {!conversationOnly && <p className="text-xs text-[#C7D2E8]">A copy was saved first, so nothing is lost for good.</p>}
                </div>
            ) : (
                <>
                    <label className="flex items-start gap-2 text-sm">
                        <input type="checkbox" checked={conversationOnly} onChange={(event) => setConversationOnly(event.target.checked)} />
                        <span>Reset AI conversation only — close open conversations and clear AI pauses. Leave everything else unchanged.</span>
                    </label>
                    {!conversationOnly && <><p className="text-sm">
                        Think of it as wiping the whiteboard so this test phone or email walks in like a brand-new visitor.
                        A copy of everything is saved first.
                    </p>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                        <li>Clears its conversations, messages and mirrored texts.</li>
                        <li>Clears opt-out (STOP), AI pauses and first-text history, so the next first text says &ldquo;Reply STOP to opt out.&rdquo; again.</li>
                        <li>Removes its TEST bookings.</li>
                    </ul>
                    <label className="flex items-start gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={removePortalAccess}
                            onChange={(event) => setRemovePortalAccess(event.target.checked)}
                            data-testid="reset-test-contact-remove-portal"
                        />
                        <span>Also remove this test contact's client portal access (the login and trips are not deleted).</span>
                    </label>
                    <p className="text-xs text-[#C7D2E8]">Anything tied to a real client, invoice or payment is left alone. This works only on Test list contacts.</p></>}
                    {error && <p role="alert" className="text-xs font-bold text-[#FCA5A5]">{error}</p>}
                </>
            )}
        </Modal3D>
    );
}
